import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormControl,
	UntypedFormGroup
} from '@angular/forms';

export class LodashUtilityService {
	static merge(target: any, ...sources: Array<any>): any {
		if (target === undefined) {
			return sources.find((src) => src !== undefined) || {};
		}
		if (!sources.length) {
			return target;
		}

		const source = sources.shift();

		for (const key in source) {
			if (Object.prototype.hasOwnProperty.call(source, key)) {
				if (source[key] instanceof Object && key in target) {
					target[key] = LodashUtilityService.merge(target[key], source[key]);
				} else {
					target[key] = source[key];
				}
			}
		}

		return LodashUtilityService.merge(target, ...sources);
	}

	static uniqBy(
		array: Array<any>,
		iteratee: string | ((item: any) => any)
	): Array<any> {
		if (array === undefined) {
			return [];
		}
		if (typeof iteratee !== 'function' && typeof iteratee !== 'string') {
			return array;
		}

		const seen = new Set();
		const result = [];

		for (const item of array) {
			const key =
				typeof iteratee === 'function' ? iteratee(item) : item[iteratee];

			if (!seen.has(key)) {
				seen.add(key);
				result.push(item);
			}
		}

		return result;
	}

	static mapValues<T, R>(
		obj: Dictionary<T>,
		iteratee: (value: T) => R
	): Dictionary<R> {
		const result: Dictionary<R> = {};
		Object.keys(obj).forEach((key) => {
			result[key] = iteratee(obj[key]);
		});

		return result;
	}

	static groupBy<T>(
		array: Array<T>,
		iteratee: ((item: T) => string) | string
	): Dictionary<Array<T>> {
		const result: Dictionary<Array<T>> = {};

		if (!Array.isArray(array)) {
			return result;
		}

		array.forEach((item) => {
			const key: string =
				typeof iteratee === 'function' ? iteratee(item) : item[iteratee];
			if (!result[key]) {
				result[key] = [];
			}
			result[key].push(item);
		});

		return result;
	}

	static findIndex(
		array: Array<any>,
		predicate: (item: any) => boolean
	): number {
		if (array === undefined) {
			return -1;
		}
		for (let i = 0; i < array.length; i++) {
			if (predicate(array[i])) {
				return i;
			}
		}

		return -1;
	}

	static cloneAbstractControl(
		control: AbstractControl,
		seen: Map<any, any>
	): AbstractControl {
		if (seen.has(control)) {
			return seen.get(control);
		}

		let newControl: AbstractControl;

		if (control instanceof UntypedFormGroup) {
			const formGroup = new UntypedFormGroup(
				{},
				control.validator,
				control.asyncValidator
			);
			seen.set(control, formGroup);
			Object.keys(control.controls).forEach((key) => {
				formGroup.addControl(
					key,
					LodashUtilityService.cloneAbstractControl(control.controls[key], seen)
				);
			});
			newControl = formGroup;
		} else if (control instanceof UntypedFormArray) {
			const formArray = new UntypedFormArray(
				[],
				control.validator,
				control.asyncValidator
			);
			seen.set(control, formArray);
			control.controls.forEach((formControl, index) => {
				formArray.insert(
					index,
					LodashUtilityService.cloneAbstractControl(formControl, seen)
				);
			});
			newControl = formArray;
		} else if (control instanceof UntypedFormControl) {
			const formControl = new UntypedFormControl(
				control.value,
				control.validator,
				control.asyncValidator
			);
			seen.set(control, formControl);
			newControl = formControl;
		} else {
			throw new Error('Encountered invalid control type during cloning');
		}

		LodashUtilityService.copyCustomProperties(control, newControl);

		return newControl;
	}

	static copyCustomProperties(
		source: AbstractControl,
		target: AbstractControl
	): void {
		for (const prop in source) {
			if (
				source.hasOwnProperty(prop) &&
				!(source[prop] instanceof AbstractControl)
			) {
				target[prop] = source[prop];
			}
		}
	}

	static cloneDeep<T>(obj: T, seen = new Map<any, any>()): T {
		if (obj === null || typeof obj !== 'object') {
			return obj;
		}

		if (seen.has(obj)) {
			return seen.get(obj);
		}

		if (obj instanceof AbstractControl) {
			return LodashUtilityService.cloneAbstractControl(
				obj,
				seen
			) as unknown as T;
		}

		let result: any;

		if (Array.isArray(obj)) {
			result = [];
			seen.set(obj, result);
			obj.forEach((item, index) => {
				result[index] = LodashUtilityService.cloneDeep(item, seen);
			});
		} else if (obj instanceof Date) {
			result = new Date(obj.getTime());
		} else if (obj instanceof RegExp) {
			result = new RegExp(obj.source, obj.flags);
		} else {
			result = {};
			seen.set(obj, result);
			Object.keys(obj).forEach((key) => {
				result[key] = LodashUtilityService.cloneDeep(
					(obj as { [key: string]: any })[key],
					seen
				);
			});
		}

		return result;
	}

	static isNil(value: any): boolean {
		return value === null || value === undefined;
	}

	static add(a: number, b: number): number {
		return a + b;
	}

	static uniq(array: Array<any>): Array<any> {
		if (array === undefined) {
			return [];
		}

		return Array.from(new Set(array));
	}

	static range(start: number, end?: number, step = 1): Array<number> {
		if (end === undefined && start !== undefined) {
			// eslint-disable-next-line no-param-reassign
			end = start;
			// eslint-disable-next-line no-param-reassign
			start = 0;
		}

		// step should be negative if end is less than start
		if (end < start && step > 0) {
			// eslint-disable-next-line no-param-reassign
			step = step * -1;
		}

		// if the step is 0 you will never allocate the correct array length
		const stepSize = step === 0 ? 1 : step;
		const size = Math.abs(Math.ceil((end - start) / stepSize));

		// need to exit if there is nothing to return
		if (size === Infinity) {
			return [];
		}

		// step has some really odd behavior (from tests)
		//     specifically a step of 0, which seems to repeat the start?
		const incrementStep = (x, y) =>
			// tslint:disable-next-line:restrict-plus-operands
			step === 0 ? start : x + y * step;

		return Array(size).fill(start).map(incrementStep);
	}

	static forOwn(
		obj: Record<string, any>,
		iteratee: (value: any, key: string) => void
	): void {
		if (obj === undefined) {
			return;
		}
		for (const key in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, key)) {
				iteratee(obj[key], key);
			}
		}
	}

	static keys(obj: Record<string, any> | null | undefined): Array<string> {
		if (obj === undefined) {
			return [];
		}

		return Object.keys(obj);
	}

	static reduce(
		array: Array<any>,
		iteratee: (accumulator: any, current: any) => any,
		initialValue: any
	): any {
		if (array === undefined) {
			return initialValue;
		}
		let result = initialValue;

		for (const item of array) {
			result = iteratee(result, item);
		}

		return result;
	}

	static chain(value: any): any {
		// tslint:disable:no-empty
		if (value === undefined) {
			return { map: () => {}, valueOf: () => {} };
		}

		return {
			value,
			map: (iteratee: (item: any) => any) =>
				LodashUtilityService.chain(iteratee(value)),
			valueOf: () => value
		};
	}

	static toUpper(value: string): string {
		if (value === undefined) {
			return '';
		}

		return value.toUpperCase();
	}
}

type Dictionary<T> = Record<string, T>;
